import React, { useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { ROUTE_LINKS } from '../data/routePages';
import { carrierName, checkedText, inr, rateMatrix } from '../lib/rates';

const ORIGIN = 'https://sendmyparcel.in';

// Text inside one element is always a single string here (template literals,
// not "checked {date}."): the prerender snapshot merges adjacent text nodes, and
// a split text node fails hydration, which makes React re-render the page.

// Title, description and structured data for a route page. Set here rather
// than in SeoManager because they are built from the page's own data; the
// prerender snapshot keeps them in the static HTML.
const useRouteHead = (page) => {
  useEffect(() => {
    document.title = page.title;
    const setMeta = (sel, attr, value) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', 'content', page.desc);
    setMeta('meta[property="og:title"]', 'content', page.title);
    setMeta('meta[property="og:description"]', 'content', page.desc);
    setMeta('meta[name="twitter:title"]', 'content', page.title);
    setMeta('meta[name="twitter:description"]', 'content', page.desc);

    const url = ORIGIN + page.path;
    const ld = [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: page.faq.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${ORIGIN}/` },
          { '@type': 'ListItem', position: 2, name: 'International shipping', item: `${ORIGIN}/international-shipping-india/` },
          { '@type': 'ListItem', position: 3, name: page.h1, item: url },
        ],
      },
    ];
    // The prerendered HTML already carries this block; keep exactly one.
    document.querySelectorAll('script[data-route-ld]').forEach((n) => n.remove());
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-route-ld', page.key);
    tag.textContent = JSON.stringify(ld);
    document.head.appendChild(tag);
    return () => { tag.remove(); };
  }, [page]);
};

const RateTable = ({ page }) => {
  const { weights, rows } = rateMatrix(page.key);
  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full min-w-[640px] border-collapse text-sm" data-test="rate-table">
          <thead>
            <tr className="bg-gray-900 text-white">
              <th scope="col" className="text-left p-3">Service</th>
              <th scope="col" className="text-left p-3">Typical transit</th>
              {weights.map((kg) => (
                <th key={kg} scope="col" className="text-right p-3 whitespace-nowrap">{`${kg} kg`}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.carrier} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <th scope="row" className="text-left p-3 font-semibold text-gray-800">{carrierName(row.carrier)}</th>
                <td className="p-3 text-gray-600">{row.transit || 'No fixed time'}</td>
                {weights.map((kg) => (
                  <td key={kg} className="p-3 text-right tabular-nums text-gray-800 whitespace-nowrap">
                    {row.prices[kg] != null ? inr(row.prices[kg]) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm text-gray-500">
        {`Parcel prices from India, checked ${checkedText}, for a signed-out visitor. ${page.zoneNote ? `${page.zoneNote} ` : ''}`
          + 'Customs duty is extra unless the service is duty paid (DDP). Your live quote may differ.'}
      </p>
    </div>
  );
};

const LinkList = ({ links }) => (
  <ul className="mt-4 space-y-1">
    {links.map((l) => (
      <li key={l.href}>
        <a href={l.href} className="text-orange-600 hover:underline font-medium" rel={l.href.startsWith('http') ? 'noopener' : undefined}>
          {`${l.label} →`}
        </a>
      </li>
    ))}
  </ul>
);

const Section = ({ s }) => (
  <section>
    <h2 className="text-3xl font-bold text-gray-900 mb-5">{s.h2}</h2>
    {(s.paras || []).map((p) => <p key={p} className="text-gray-600 leading-relaxed mb-4">{p}</p>)}
    {s.list && (
      <ul className="space-y-3">
        {s.list.map((item) => (
          <li key={item} className="flex gap-3 text-gray-600">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
    {s.steps && (
      <ol className="grid gap-4 md:grid-cols-5">
        {s.steps.map(([title, text], i) => (
          <li key={title} className="rounded-xl border border-gray-200 p-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-sm font-bold text-white">{i + 1}</span>
            <p className="mt-3 font-semibold text-gray-900">{title}</p>
            <p className="mt-1 text-sm text-gray-600">{text}</p>
          </li>
        ))}
      </ol>
    )}
    {(s.after || []).map((p) => <p key={p} className="text-gray-600 leading-relaxed mt-4">{p}</p>)}
    {s.link && <LinkList links={[s.link]} />}
    {s.links && <LinkList links={s.links} />}
  </section>
);

const CourierRoutePage = ({ page }) => {
  useRouteHead(page);
  const calculator = '/shipping-rates-calculator/';

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gray-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <nav aria-label="Breadcrumb" className="text-sm text-gray-400 mb-6">
            <a href="/" className="hover:text-white">Home</a>{' › '}
            <a href="/international-shipping-india/" className="hover:text-white">International shipping</a>{' › '}
            <span className="text-gray-200">{page.h1}</span>
          </nav>
          <img src={page.flag.src} alt={page.flag.alt} width="72" height="54" className="mx-auto mb-4 rounded shadow" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{page.h1}</h1>
          <p className="text-xl text-orange-400 font-medium">{page.sub}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="/quote/" className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
              Get a quote
            </a>
            <a href={calculator} className="inline-block border border-white/40 hover:bg-white/10 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
              Shipping calculator
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-14 space-y-14">
        <section className="rounded-2xl border border-orange-200 bg-orange-50 p-6" data-test="quick-answer">
          <h2 className="text-xl font-bold text-gray-900 mb-2">The short answer</h2>
          <p className="text-gray-700 leading-relaxed">{page.quick}</p>
          <p className="mt-2 text-sm text-gray-500">{`Prices checked ${checkedText}.`}</p>
        </section>

        <section>
          {page.intro.map((p) => <p key={p} className="text-gray-600 leading-relaxed mb-4 text-lg">{p}</p>)}
        </section>

        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-5">{`${page.h1.replace('Courier from ', '')} courier rates by weight`}</h2>
          <RateTable page={page} />
          <p className="mt-4 text-gray-600">
            {'Need a price for a different weight or box size? '}
            <a href={calculator} className="text-orange-600 hover:underline font-medium">Use the shipping calculator</a>
            {' or '}
            <a href="/quote/" className="text-orange-600 hover:underline font-medium">get an instant quote</a>
            {'.'}
          </p>
        </section>

        {page.sections.map((s) => <Section key={s.h2} s={s} />)}

        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {page.faq.map(({ q, a }) => (
              <div key={q} className="border-b border-gray-200 pb-6">
                <h3 className="font-semibold text-gray-800 mb-2 text-lg">{q}</h3>
                <p className="text-gray-600">{a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gray-50 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Other routes from India</h2>
          <p className="text-gray-600 text-sm">
            {page.related.map((k, i) => (
              <React.Fragment key={k}>
                {i > 0 && ' · '}
                <a href={ROUTE_LINKS[k].href} className="text-orange-600 hover:underline font-medium">{ROUTE_LINKS[k].label}</a>
              </React.Fragment>
            ))}
            {' · '}
            <a href="/international-shipping-india/" className="text-orange-600 hover:underline font-medium">All destinations</a>
          </p>
        </section>

        <section className="text-center bg-orange-500 rounded-2xl py-12 px-6 text-white">
          <h2 className="text-2xl font-bold mb-3">{`Ready to ship to ${page.h1.replace('Courier from India to ', '')}?`}</h2>
          <p className="mb-6 text-orange-100">Compare every service on the route and book in minutes.</p>
          <a href="/quote/" className="inline-block bg-white text-orange-600 font-semibold px-8 py-3 rounded-lg hover:bg-orange-50 transition-colors">
            Get a quote
          </a>
        </section>
      </div>
    </div>
  );
};

export default CourierRoutePage;
